import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { forkJoin, of, Subscription } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { AlertService } from "../services/alert.service";
import { EnvService } from "../services/env.service";
import { MatTableDataSource } from "@angular/material/table";
import { AuthService } from "../services/auth.service";

@Component({
  selector: "micro-cmdb-server-request-detail",
  templateUrl: "./cmdb-server-request-detail.component.html",
})
export class CmdbServerRequestDetailComponent implements OnInit, OnDestroy {
  @Input() hardwareRequestId: any;
  @Input() serverRequestId: any;
  @Output() persisted = new EventEmitter<any>();

  dataSource = new MatTableDataSource<any>();

  serverRequest: any;
  hardwareRequest: any;

  selectedValues: any = {};
  osVersionOptions: any[] = [];
  osOptions: any[] = [];
  private embedded = false;
  validatingCompany = false;
  companyValidationFailed = false;
  parentMismatch = false;
  private fixedHardwareRequest: any;
  private validationRequest?: Subscription;
  private loadRequest?: Subscription;
  private subscriptions = new Subscription();
  private readonly companyFields = [
    { id: 'requestId', endpoint: 'cmdbHardwareRequests', name: 'requestName' },
    { id: 'environmentId', endpoint: 'environments', name: 'environmentName' },
    { id: 'osId', endpoint: 'cmdbOsFamily', name: 'osName' },
    { id: 'osVersionId', endpoint: 'cmdbOsVersions', name: 'osVersionName' },
    { id: 'siteId', endpoint: 'sites', name: 'siteName' },
    { id: 'hostingPlatformId', endpoint: 'cmdbHostingPlatform', name: 'hostingPlatformName' }
  ];

  get hasRequiredSelections(): boolean {
    return !!this.serverRequest?.companyId && !!this.serverRequest?.serverSize
      && this.companyFields.every(field => !!this.serverRequest[field.id]);
  }

  constructor(
    public auth: AuthService,
    private env: EnvService,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.embedded = this.serverRequestId !== undefined && this.serverRequestId !== null;
    if (this.embedded) {
      String(this.serverRequestId) === "0" ? this.createNew() : this.load(this.serverRequestId);
    } else {
      this.subscriptions.add(this.route.params.subscribe((params: Params) => {
        this.hardwareRequestId = params["hardwareRequestId"];
        this.serverRequestId = params["serverRequestId"];
        this.serverRequestId === "0" ? this.createNew() : this.load(this.serverRequestId);
      }));
    }
  }

  private resetSelections(): void {
    this.loadRequest?.unsubscribe();
    this.validationRequest?.unsubscribe();
    this.selectedValues = {};
    this.osOptions = [];
    this.osVersionOptions = [];
    this.hardwareRequest = undefined;
    this.fixedHardwareRequest = undefined;
    this.validatingCompany = false;
    this.companyValidationFailed = false;
    this.parentMismatch = false;
  }

  load(id: string): void {
    this.resetSelections();
    this.serverRequest = undefined;
    this.loadRequest = this.http.get(`${this.env.e.url}/domain/cmdbServerRequests/${id}`)
      .subscribe(data => {
        this.serverRequest = data;
        this.validateCompanySelections();
      });
  }

  createNew(): void {
    this.resetSelections();
    this.serverRequest = {
      requestId: this.hardwareRequestId,
      requestedBy: this.auth.getUserId(),
      companyId: this.hardwareRequestId ? undefined : this.auth.getCompanyId()
    };
    this.validateCompanySelections();
  }

  save(): void {
    if (this.validatingCompany || this.companyValidationFailed || this.parentMismatch || !this.hasRequiredSelections) return;
    const params = new HttpParams().set("companyId", this.serverRequest.companyId);
    const isUpdate = !!this.serverRequest.id;
    const request = isUpdate
      ? this.http.put(
          `${this.env.e.url}/domain/cmdbServerRequests/${this.serverRequest.id}`,
          this.serverRequest,
          { params },
        )
      : this.http.post(
          `${this.env.e.url}/domain/cmdbServerRequests`,
          this.serverRequest,
          { params },
        );
    this.subscriptions.add(request.subscribe((data) => {
      this.serverRequest = data;
      this.validateCompanySelections();
      this.alertService.info(
        `${isUpdate ? "Updated" : "Created"} ${this.serverRequest.serverName} request`,
      );
      this.persisted.emit({ data, action: isUpdate ? "updated" : "added" });
      if (!this.embedded && !isUpdate) {
        this.router.navigate(["/hardwareRequests", this.hardwareRequestId || this.serverRequest.requestId, "serverRequest", this.serverRequest.id]);
      }
    }));
  }

  delete(): void {
    this.http
      .delete(`${this.env.e.url}/domain/cmdbServerRequests/${this.serverRequest.id}`)
      .subscribe(() => {
        this.persisted.emit({ data: this.serverRequest, action: "deleted" });
        this.alertService.info(`Deleted ${this.serverRequest.name}`);
        if (!this.embedded) {
          this.router.navigate(["/hardwareRequests", this.hardwareRequestId]);
        }
      });
  }

  onCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.serverRequest.active = target.checked ? "Y" : "N";
  }

  onSelectedChange(key: string, value: any): void {
    this.selectedValues[key] = value;
    const field = this.companyFields.find(field => field.id === key);
    if (field) this.serverRequest[field.name] = value?.name || null;
  }

  onHardwareRequestChange(hardwareRequest: any): void {
    this.onSelectedChange('requestId', hardwareRequest);
    this.validateCompanySelections();
  }

  onOsChange(osFamily: any): void {
    this.onSelectedChange('osId', osFamily);
    this.validateCompanySelections();
  }

  onCompanyChange(companyId: any): void {
    if (this.hardwareRequestId || companyId === this.serverRequest.companyId) return;
    this.serverRequest.companyId = companyId;
    this.validateCompanySelections();
  }

  useParentHardwareRequest(): void {
    if (!this.fixedHardwareRequest?.companyId) return;
    this.serverRequest.requestId = this.fixedHardwareRequest.id;
    this.serverRequest.companyId = this.fixedHardwareRequest.companyId;
    this.validateCompanySelections();
  }

  validateCompanySelections(): void {
    this.validationRequest?.unsubscribe();
    this.companyValidationFailed = false;
    this.parentMismatch = false;
    this.osOptions = [];
    this.osVersionOptions = [];
    const request = this.serverRequest;
    const originalCompanyId = request.companyId;
    const parentId = this.hardwareRequestId;
    const fields = this.companyFields.map(field => ({ ...field, value: request[field.id] }));
    const selections = fields.map(field => field.value
      ? this.http.get<any>(`${this.env.e.url}/domain/${field.endpoint}/${field.value}`)
      : of(null));
    this.validatingCompany = true;

    this.validationRequest = forkJoin({
      records: forkJoin(selections),
      parent: parentId && parentId !== request.requestId
        ? this.http.get<any>(`${this.env.e.url}/domain/cmdbHardwareRequests/${parentId}`)
        : of(null)
    }).pipe(switchMap(({ records, parent }) => {
      const [hardware, , os] = records;
      const fixedParent = parentId ? (parent || hardware) : undefined;
      const companyId = !request.id && fixedParent?.companyId ? fixedParent.companyId : originalCompanyId;
      const parentMismatch = !!parentId && !!fixedParent &&
        (request.requestId !== parentId || companyId !== fixedParent.companyId);
      const hardwareMatches = !!companyId && hardware?.companyId === companyId;
      const osTypeMismatch = hardwareMatches && !!hardware.osTypeId && !!os && os.osTypeId !== hardware.osTypeId;
      const osMatches = !!companyId && os?.companyId === companyId && !osTypeMismatch;
      const context = { records, fixedParent, companyId, parentMismatch, osTypeMismatch };
      return forkJoin({
        osOptions: !parentMismatch && hardwareMatches && hardware.osTypeId
          ? this.http.get<any[]>(`${this.env.e.url}/domain/cmdbOsFamily/all`, {
              params: { companyId, osTypeId: hardware.osTypeId }
            }) : of([] as any[]),
        versionOptions: !parentMismatch && osMatches
          ? this.http.get<any[]>(`${this.env.e.url}/domain/cmdbOsVersions/all`, {
              params: { companyId, osFamily: os.name }
            }).pipe(map(options => options.filter(option => option.companyId === companyId && option.osFamilyId === os.id)))
          : of([] as any[])
      }).pipe(map(options => ({ ...context, ...options })));
    })).subscribe({
      next: result => {
        if (this.serverRequest !== request || request.companyId !== originalCompanyId) return;
        const { records, fixedParent, companyId, parentMismatch, osTypeMismatch } = result;
        this.fixedHardwareRequest = fixedParent;
        this.parentMismatch = parentMismatch;
        request.companyId = companyId;
        let unresolved = !!parentId && !fixedParent?.companyId;
        if (companyId && !parentMismatch) {
          fields.forEach((field, index) => {
            const record = records[index];
            if (field.value && !record?.companyId) unresolved = true;
            if (record?.companyId && record.companyId !== companyId) request[field.id] = null;
          });
          const os = records[2];
          const version = records[3];
          if (osTypeMismatch) request.osId = null;
          if (version && (osTypeMismatch || (os && version.osFamilyId !== os.id))) request.osVersionId = null;
        }
        fields.forEach((field, index) => {
          const selected = request[field.id] ? records[index] : undefined;
          this.selectedValues[field.id] = selected;
          request[field.name] = selected?.name || null;
        });
        this.hardwareRequest = this.selectedValues.requestId;
        this.osOptions = result.osOptions;
        this.osVersionOptions = result.versionOptions;
        this.companyValidationFailed = unresolved;
        this.validatingCompany = false;
      },
      error: () => {
        this.validatingCompany = false;
        this.companyValidationFailed = true;
        this.alertService.warn('Unable to validate the selected records for this company. Please retry.');
      }
    });
  }

  ngOnDestroy(): void {
    this.validationRequest?.unsubscribe();
    this.loadRequest?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  change(event: Event) {
    const target = event.target as HTMLInputElement;
    this.serverRequest.active = target.checked ? "Y" : "N";
  }
}
